import React, { useState } from "react";
import { initiatePasswordReset, confirmPasswordReset } from "../auth/cognitoAuth";
import "../index.css";

interface ForgotPasswordProps {
  onCancel: () => void;
  onSuccess: () => void;
}

// Brand presentation tokens — mirror the sign-in card aesthetic.
const BRAND_INK = "#1A1A1A";
const BRAND_CORAL = "#E05A3A";
const FONT_DISPLAY = "'Fredoka', sans-serif";
const FONT_BODY = "'Nunito', sans-serif";

const cardStyle: React.CSSProperties = {
  background: "#FFFFFF",
  border: `3px solid ${BRAND_INK}`,
  borderRadius: 20,
  boxShadow: `6px 6px 0px ${BRAND_INK}`,
  padding: "28px 24px",
  maxWidth: 380,
  margin: "24px auto 0",
  boxSizing: "border-box",
};

const headingStyle: React.CSSProperties = {
  fontFamily: FONT_DISPLAY,
  fontSize: 24,
  fontWeight: 700,
  color: BRAND_INK,
  textAlign: "center",
  marginBottom: 20,
  marginTop: 0,
};

const pillInputStyle: React.CSSProperties = {
  width: "100%",
  border: `2.5px solid ${BRAND_CORAL}`,
  borderRadius: 100,
  padding: "14px 20px",
  fontFamily: FONT_DISPLAY,
  fontSize: 16,
  background: "#FDF6EE",
  color: BRAND_INK,
  outline: "none",
  marginBottom: 12,
  boxSizing: "border-box",
  boxShadow: "none",
};

// Same as pillInputStyle but with extra right padding so the show/hide
// eye icon can sit absolutely positioned without clipping the text.
const pillPasswordInputStyle: React.CSSProperties = {
  ...pillInputStyle,
  padding: "14px 48px 14px 20px",
};

const submitPillStyle: React.CSSProperties = {
  background: BRAND_CORAL,
  color: "#FFFFFF",
  border: `3px solid ${BRAND_INK}`,
  borderRadius: 100,
  boxShadow: `4px 4px 0px ${BRAND_INK}`,
  padding: "14px 36px",
  fontFamily: FONT_DISPLAY,
  fontSize: 18,
  fontWeight: 700,
  width: "100%",
  marginTop: 4,
  cursor: "pointer",
  boxSizing: "border-box",
  transition: "transform 0.12s ease, box-shadow 0.12s ease, background 0.12s ease",
};

const backLinkStyle: React.CSSProperties = {
  fontFamily: FONT_BODY,
  fontSize: 13,
  color: "#888",
  textAlign: "center",
  display: "block",
  marginTop: 16,
  marginBottom: 0,
  textDecoration: "none",
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",
  width: "100%",
};

const errorTextStyle: React.CSSProperties = {
  fontFamily: FONT_BODY,
  fontSize: 13,
  color: "#C44B2E",
  textAlign: "center",
  marginBottom: 12,
  marginTop: 0,
};

const successTextStyle: React.CSSProperties = {
  fontFamily: FONT_BODY,
  fontSize: 13,
  color: BRAND_CORAL,
  textAlign: "center",
  marginBottom: 12,
  marginTop: 0,
};

const helperTextStyle: React.CSSProperties = {
  fontFamily: FONT_BODY,
  fontSize: 13,
  color: "#555",
  textAlign: "center",
  marginBottom: 12,
  marginTop: 0,
};

const ForgotPassword: React.FC<ForgotPasswordProps> = ({ onCancel, onSuccess }) => {
  const [email, setEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [step, setStep] = useState<"email" | "reset">("email");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Password validation
  const isPasswordValid =
    newPassword.length >= 8 &&
    /[A-Z]/.test(newPassword) &&
    /[a-z]/.test(newPassword) &&
    /[0-9]/.test(newPassword) &&
    /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);

  const passwordsMatch = newPassword === confirmPassword;

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");
    setIsLoading(true);

    if (!email.trim()) {
      setErrorMessage("Please enter your email address.");
      setIsLoading(false);
      return;
    }

    try {
      const result = await initiatePasswordReset(email);
      if (result.success) {
        setSuccessMessage("Reset code sent! Check your email.");
        setStep("reset");
      } else {
        setErrorMessage(result.error || "Failed to send reset code. Please try again.");
      }
    } catch (error) {
      setErrorMessage("Failed to send reset code. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");
    setIsLoading(true);

    if (!resetCode.trim()) {
      setErrorMessage("Please enter the reset code.");
      setIsLoading(false);
      return;
    }

    if (!isPasswordValid) {
      setErrorMessage("Password does not meet requirements.");
      setIsLoading(false);
      return;
    }

    if (!passwordsMatch) {
      setErrorMessage("Passwords do not match.");
      setIsLoading(false);
      return;
    }

    try {
      const result = await confirmPasswordReset(email, resetCode, newPassword);
      if (result.success) {
        setSuccessMessage("Password reset successful! You can now sign in.");
        setTimeout(() => {
          onSuccess();
        }, 2000);
      } else {
        setErrorMessage(result.error || "Failed to reset password. Please try again.");
      }
    } catch (error) {
      setErrorMessage("Failed to reset password. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ paddingTop: 16 }}>
    <div style={cardStyle}>
      <h2 style={headingStyle}>
        {step === "email" ? "Reset Password" : "Forgot Password"}
      </h2>

      {errorMessage && <p style={errorTextStyle}>{errorMessage}</p>}
      {successMessage && <p style={successTextStyle}>{successMessage}</p>}

      {step === "email" ? (
        <form onSubmit={handleSendCode}>
          <input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setErrorMessage("");
            }}
            placeholder="Enter your email"
            style={pillInputStyle}
            required
            autoFocus
            autoComplete="username"
          />

          <button
            type="submit"
            disabled={isLoading}
            style={{
              ...submitPillStyle,
              opacity: isLoading ? 0.6 : 1,
              cursor: isLoading ? "not-allowed" : "pointer",
            }}
          >
            {isLoading ? "Sending..." : "Send Reset Code"}
          </button>

          <button type="button" onClick={onCancel} style={backLinkStyle}>
            Back to Sign In
          </button>
        </form>
      ) : (
        <form onSubmit={handleResetPassword}>
          <p style={helperTextStyle}>
            Enter the code sent to <strong>{email}</strong> and your new password.
          </p>

          {/* Hidden email field to help browser understand this is for password reset */}
          <input
            type="email"
            value={email}
            readOnly
            autoComplete="username"
            style={{ display: "none" }}
            tabIndex={-1}
          />

          <input
            type="text"
            value={resetCode}
            onChange={(e) => {
              setResetCode(e.target.value);
              setErrorMessage("");
            }}
            placeholder="Enter reset code"
            style={pillInputStyle}
            required
            autoFocus
            autoComplete="off"
          />

          <div style={{ position: "relative" }}>
            <input
              type={showNewPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                setErrorMessage("");
              }}
              placeholder="Enter new password"
              style={pillPasswordInputStyle}
              required
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-0"
              aria-label={showNewPassword ? "Hide password" : "Show password"}
              style={{ top: "calc(50% - 6px)" }}
            >
              {showNewPassword ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>

          {/* Password requirements */}
          {newPassword && !isPasswordValid && (
            <div className="validity-list mb-3">
              {[
                { text: "8+ characters", ok: newPassword.length >= 8 },
                { text: "Uppercase letter", ok: /[A-Z]/.test(newPassword) },
                { text: "Lowercase letter", ok: /[a-z]/.test(newPassword) },
                { text: "Number", ok: /[0-9]/.test(newPassword) },
                { text: "Special character", ok: /[!@#$%^&*(),.?\":{}|<>]/.test(newPassword) },
              ].map((item, i) => (
                <div key={i} className={`validity-item ${item.ok ? "is-ok" : ""}`}>
                  <span className="mark">{item.ok ? "✓" : "✗"}</span>
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
          )}

          <div style={{ position: "relative" }}>
            <input
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setErrorMessage("");
              }}
              placeholder="Confirm new password"
              style={pillPasswordInputStyle}
              required
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-0"
              aria-label={showConfirmPassword ? "Hide password" : "Show password"}
              style={{ top: "calc(50% - 6px)" }}
            >
              {showConfirmPassword ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>

          {confirmPassword && !passwordsMatch && (
            <p style={errorTextStyle}>Passwords do not match</p>
          )}

          <button
            type="submit"
            disabled={isLoading || !isPasswordValid || !passwordsMatch}
            style={{
              ...submitPillStyle,
              opacity: isLoading || !isPasswordValid || !passwordsMatch ? 0.6 : 1,
              cursor:
                isLoading || !isPasswordValid || !passwordsMatch ? "not-allowed" : "pointer",
            }}
          >
            {isLoading ? "Resetting..." : "Reset Password"}
          </button>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 12,
              marginTop: 16,
            }}
          >
            <button
              type="button"
              onClick={() => setStep("email")}
              style={{ ...backLinkStyle, marginTop: 0, width: "auto", display: "inline" }}
            >
              Use different email
            </button>
            <span
              style={{ fontFamily: FONT_BODY, fontSize: 13, color: "#888", lineHeight: 1 }}
              aria-hidden="true"
            >
              |
            </span>
            <button
              type="button"
              onClick={onCancel}
              style={{ ...backLinkStyle, marginTop: 0, width: "auto", display: "inline" }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
    </div>
  );
};

export default ForgotPassword;
